'use client'

import React, { useEffect, useMemo, useState } from 'react'
import {
  UploadDropzone,
  UploaderState,
} from '@pageFeatures/design/designUpload/components/UploadDropzone/UploadDropzone'
import isEmpty from 'lodash/isEmpty'
import {
  EditorContentfulConfiguration,
  GuidelineConfigurationWithAsset,
  ProductVariant,
  TOnlineEditorConfiguration,
} from '@pageFeatures/design/designUpload/types'

import parentLogger from '@utils/logger'
import { SummaryStickyFooter } from '@components/common/SummaryStickyFooter'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { formatPrice } from '@commerce/product/use-price'
import {
  NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
  NEXT_PUBLIC_TAX_TYPE,
} from '@utils/client-side-config'
import { GuidelineDownload } from '@pageFeatures/design/designUpload/components/GuidelineDownload'
import useUploaderToCart from '@pageFeatures/design/designUpload/hooks/useUploaderToCart/useUploaderToCart'
import usePreloadEditorConfiguration from '@pageFeatures/design/designUpload/hooks/usePreloadEditorConfiguration'
import useGuidelines from '@pageFeatures/design/designUpload/hooks/useGuidelines'
import useCart from '@framework/cart/use-cart'
import {
  RadioTileGroup,
  IRadioTileOption,
} from '@noissue-ui-kit/oldBranding/radioTile'
import { DESIGN_PATHS } from '@pageFeatures/design/constants'
import { BriefingForm } from '@pageFeatures/design/briefingForm/components/BriefingForm'
import { parseBriefConfiguration } from '@pageFeatures/design/briefingForm/utils/parseBriefConfiguration'
import { IBriefStep } from '@pageFeatures/design/briefingForm/types'
import { BriefingFormDataProvider } from '@pageFeatures/design/briefingForm/components/BriefingFormDataProvider'
import {
  getDesignerChatbotConfigs,
  TDesignChatBotUseCases,
} from '@pageFeatures/design/briefingForm/utils/parseDesignerChatbotConfiguration'
import { Modal } from '@noissue-ui-kit/modal'
import { briefingFormWasUpdated } from '@pageFeatures/design/briefingForm/utils'
import { trackDesignChoice, trackDesignerFileUpload } from '@utils/gtm'
import { TDesignJourney, TOnlineEditorState } from '@pageFeatures/design/types'
import {
  LegacyV3EditorAlertMessage,
  checkIfLegacyEditor,
} from '@pageFeatures/design/legacyEditors.utils'

import { EditorWrapper } from '@pageFeatures/design/onlineEditor/EditorWrapper'
import { editorProductsConfig } from '@pageFeatures/design/onlineEditor/editor-products.config'
import { UnifiedEditorProvider } from '@pageFeatures/design/UnifiedEditorProvider'
import { IPdfGenerationPayload } from '@pageFeatures/design/onlineEditor/editor.types'
import { getOnlineEditorCustomizationObject } from '@pageFeatures/design/onlineEditor/editor.utils'
import {
  DesignerChatbot,
  IDesignerChatbot,
} from '@pageFeatures/design/DesignerChatbot'
import { legacyDesignJourneyToNew } from '@pageFeatures/design/utils'
import { UploadDropzoneProvider } from '@pageFeatures/design/designUpload/components/UploadDropzone/UploadDropzone.provider'
import { UPLOADER_FALLBACK_COPY } from '@pageFeatures/design/designUpload/components/UploadDropzone/UploadDropzone.utils'
import { FormCheckbox } from 'noissueUiKit/form/formCheckbox'
import { Transition } from '@headlessui/react'
import * as Sentry from '@sentry/nextjs'

import {
  Breadcrumbs,
  BreadcrumbsItem,
} from '@noissue-ui-kit/oldBranding/breadcrumbs'
import {
  breadcrumbsLinksToStructuredData,
  getPageBreadcrumbs,
} from '@utils/breadcrumbs'
import { IBreadcrumbLink } from 'types/breadcrumbs'
import { BreadcrumbJsonLd } from 'next-seo'
import { getLocale } from '@utils/contentful'
import { FormFieldTooltip } from '@noissue-ui-kit/form/form/FormFieldTooltip'
import { ProductOptionsSummary } from './ProductOptionSummary'
import { toUploadContent } from '../_utils'
import { GenericContentBlock } from 'types/contentful/genericContentBlock'
import { getProductOptionSummary } from './ProductOptionSummary/ProductOptionSummary.utils'
import { useUI } from 'app/_components/ui'
import useProductOptionSelection from '@pageFeatures/design/designUpload/hooks/useProductOptionSelection/useProductOptionSelection'

const logger = parentLogger.child({ name: 'upload-[slug].tsx' })

export default function UploadPageClient({
  editorConfiguration,
  productVariantsSales,
  guidelineConfigurationWithLinkedAssets,
}: {
  editorConfiguration: EditorContentfulConfiguration
  productVariantsSales: ProductVariant[]
  guidelineConfigurationWithLinkedAssets: GuidelineConfigurationWithAsset
}) {
  const router = useRouter()
  const pathname = usePathname()
  const query = useSearchParams()
  const queryObject = useMemo(() => {
    const entries = query?.entries() || []
    return Object.fromEntries(entries)
  }, [query])
  const locale = getLocale()

  const {
    urlSlug,
    productName,
    productSlug,
    uploaderIdleStateContent,
    uploaderDragOverStateContent,
    uploaderSuccessStateContent,
    briefConfiguration,
    enableSkipArtworkDesignJourney,
    breadcrumbs: breadcrumbsConfig,
  } = editorConfiguration

  const [selectedDesignJourney, setSelectedDesignJourney] =
    useState<TDesignJourney>(DESIGN_PATHS.DESIGN_UPLOAD)

  const [briefingFormOutput, setBriefingFormOutput] = useState<
    Record<string, string | never>
  >({})

  const onlineEditorConfig = editorProductsConfig[urlSlug]
  const displayDesignPathChoice =
    !isEmpty(briefConfiguration) || !isEmpty(onlineEditorConfig)

  const parsedBriefConfiguration: IBriefStep[] =
    parseBriefConfiguration(briefConfiguration)

  const designerChatbotConfigs: Record<
    TDesignChatBotUseCases,
    IDesignerChatbot
  > = getDesignerChatbotConfigs(editorConfiguration)

  const { preloadConfiguration, configurationIsLoading } =
    usePreloadEditorConfiguration({
      fieldsVisibilityConfiguration: editorConfiguration.fieldsConfiguration,
      query: queryObject as any,
      productVariantsSales,
    })

  const {
    state: optionSelectionState,
    productOptionSelected,
    upload,
    removeUploadFile,
    isDesignReadyForAddToCart,
    getFileUploadedUrls,
    editingItemId,
    setPreloadConfiguration,
    clearUploadedFiles,
  } = useProductOptionSelection({
    productCategorySlug: productSlug,
    productVariantsSales,
    editorConfiguration,
  })

  const [addItemToCart] = useUploaderToCart()
  const { openSidebar } = useUI()
  const { data: cartContent } = useCart()
  const designIsUploaded = isDesignReadyForAddToCart()
  const readOnlyMode = query?.get('readOnly') === 'true'
  const [preloadedBriefConfig, setPreloadedBriefConfig] = useState(null)
  const [
    needDesignJourneyChangeConfirmation,
    setNeedDesignJourneyChangeConfirmation,
  ] = useState<boolean>(false)
  const [pendingDesignJourneyChoice, setPendingDesignJourneyChoice] =
    useState<IRadioTileOption>(null)
  const [pdfGenerationPayload, setPdfGenerationPayload] =
    useState<IPdfGenerationPayload>(null)
  const [onlineEditorOrderPayload, setOnlineEditorOrderPayload] =
    useState<TOnlineEditorConfiguration>(null)
  const [preloadedOnlineEditorConfig, setPreloadedOnlineEditorConfig] =
    useState<TOnlineEditorConfiguration>(null)
  const [purchaseInProgress, setPurchaseInProgress] = useState<boolean>(false)
  const [addToCartError, setAddToCartError] = useState<string>(null)
  const [editorState, setEditorState] = useState<TOnlineEditorState>('initial')
  const [qrCodeChoice, setQrCodeChoice] = useState<string>(null)

  useEffect(() => {
    if (isEmpty(preloadConfiguration)) {
      return
    }

    setPreloadConfiguration(preloadConfiguration)
  }, [preloadConfiguration])

  const productOption = productOptionSelected()

  const { guidelineAssets, selectedVariantOptions } = useGuidelines({
    guidelineConfiguration: guidelineConfigurationWithLinkedAssets,
    selectedVariant: productOption,
  })

  useEffect(() => {
    if (!isEmpty(preloadConfiguration?.brief)) {
      setPreloadedBriefConfig(preloadConfiguration.brief)
    }

    if (!isEmpty(preloadConfiguration?.onlineEditor)) {
      setPreloadedOnlineEditorConfig(preloadConfiguration?.onlineEditor)
    }

    let legacyOptionSuccessorIsAvailable = true
    const convertedLegacyJourney =
      legacyDesignJourneyToNew[preloadConfiguration?.version]

    if (convertedLegacyJourney === 'editor') {
      legacyOptionSuccessorIsAvailable = Boolean(
        editorProductsConfig[productSlug]
      )
    }

    const preloadedDesignJourney =
      preloadConfiguration?.designJourney ||
      (legacyOptionSuccessorIsAvailable
        ? legacyDesignJourneyToNew[preloadConfiguration?.version]
        : null)

    if (preloadedDesignJourney) {
      setSelectedDesignJourney(preloadedDesignJourney)
    }
  }, [preloadConfiguration])

  useEffect(() => {
    if (
      briefingFormOutput &&
      briefingFormWasUpdated({
        briefingFormOutput,
      })
    ) {
      setNeedDesignJourneyChangeConfirmation(true)
    }
  }, [briefingFormOutput])

  const clearSelectedDesignJourneyData = (designPath) => {
    switch (designPath.key) {
      case 'briefing-form':
        setBriefingFormOutput(null)
        break
      default:
        break
    }
  }

  const trackUploadedFile = ({ fileUrl }) => {
    trackDesignerFileUpload({
      fileUrl,
      designJourney: selectedDesignJourney as TDesignJourney,
      productSlug,
    })
  }

  useEffect(() => {
    if (selectedDesignJourney) {
      trackDesignChoice({
        selectedOptionLabel: selectedDesignJourney,
        selectedProductSlug: productSlug,
      })
    } else {
      setNeedDesignJourneyChangeConfirmation(false)
    }
    setEditorState('initial')
  }, [selectedDesignJourney])

  const designPathsOptions: IRadioTileOption[] = [
    {
      key: DESIGN_PATHS.DESIGN_UPLOAD,
      displayLabel: 'Upload artwork',
    },
  ]

  if (!isEmpty(onlineEditorConfig)) {
    const onlineEditorLabel = onlineEditorConfig?.enabledWidgets?.logo
      ? 'Customize artwork'
      : 'Create pattern'

    designPathsOptions.push({
      key: DESIGN_PATHS.ONLINE_EDITOR,
      displayLabel: onlineEditorLabel,
    })
  }

  if (!isEmpty(briefConfiguration)) {
    designPathsOptions.push({
      key: DESIGN_PATHS.BRIEFING_FORM,
      displayLabel: 'Brief a designer',
    })
  }

  const canPurchase =
    (designIsUploaded || selectedDesignJourney === DESIGN_PATHS.SKIP_ARTWORK) &&
    editorState !== 'error' &&
    editorState !== 'loading'

  const breadcrumbsLinks: IBreadcrumbLink[] = useMemo(() => {
    return getPageBreadcrumbs([
      ...(breadcrumbsConfig?.map((x) => ({
        name: x.fields.ctaLabel,
        path: x.fields.ctaLink,
      })) || []),
      {
        path: pathname,
        name: 'Design',
      },
    ])
  }, [breadcrumbsConfig])

  const selectedOptionsSummary = useMemo(
    () => getProductOptionSummary({ productOption, locale }),
    [productOption, locale]
  )

  if (configurationIsLoading) {
    return (
      <div className="fixed top-0 bottom-0 left-0 right-0 z-20 flex items-center justify-center bg-white opacity-80">
        <img
          className="w-40 h-40 -translate-y-1/2 opacity-100 top-1/2 left-1/2"
          src={'/icon-uploading-new-brand.gif'}
          alt="uploading"
        />
      </div>
    )
  }

  return (
    <>
      <UploadDropzoneProvider
        initialFileUrls={preloadConfiguration.filesUrls}
        forceReset={optionSelectionState?.forceResetUploadDropzone}
      >
        <UnifiedEditorProvider
          selectedDesignJourney={selectedDesignJourney}
          designerChatbotConfigs={designerChatbotConfigs}
          editorState={editorState}
          setEditorState={setEditorState}
        >
          {breadcrumbsLinks?.length > 0 && (
            <Breadcrumbs className="mx-auto py-6 h-20 max-w-screen-xl px-5 sm:px-16">
              {breadcrumbsLinks.map((breadcrumb, index) => (
                <BreadcrumbsItem
                  key={`breadcrumbsItem-${index}`}
                  href={breadcrumb.url}
                  text={breadcrumb.name}
                />
              ))}
            </Breadcrumbs>
          )}

          <BreadcrumbJsonLd
            itemListElements={breadcrumbsLinksToStructuredData(
              breadcrumbsLinks
            )}
          />

          <div className="lg:overflow-visible">
            <div className="flex flex-wrap w-full max-w-screen-xl px-4 m-auto overflow-visible md:px-16 lg:flex-nowrap">
              <div className="w-full lg:mr-6 lg:mb-36 md:w-1/2">
                {checkIfLegacyEditor(preloadConfiguration) && (
                  <LegacyV3EditorAlertMessage />
                )}
                <div className="flex-1 w-full p-0 mr-6 md:mb-36 sm:p-16 sm:bg-black-5 rounded-4xl">
                  <h1 className="mb-8 text-[24px] font-semibold text-acai font-mori">
                    Upload your artwork
                  </h1>

                  <ProductOptionsSummary
                    productName={productName}
                    pdpSlug={`/custom-packaging/${productSlug}`}
                    imageUrl={
                      productOption?.imageUrl ||
                      optionSelectionState?.productVariantsSales[0]?.imageUrl
                    }
                    selectedOptions={selectedOptionsSummary}
                  />

                  <hr className="my-8 bg-black-20 w-full" />

                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                    <h3 className="text-[16px] font-semibold text-acai mb-2 sm:mb-0">
                      Choose how to design your packaging
                    </h3>

                    <FormFieldTooltip
                      snapToRight={true}
                      className="ml-auto"
                      customLabel={
                        <span className="underline text-black-80 text-xl">
                          Will I get a proof?
                        </span>
                      }
                    >
                      <p className="text-[14px] text-acai font-normal">
                        You can opt for a free 'Expert Print Check & Proof'
                        during checkout
                      </p>
                    </FormFieldTooltip>
                  </div>

                  <div className="accordion-container">
                    {displayDesignPathChoice ? (
                      <>
                        {enableSkipArtworkDesignJourney && (
                          <FormCheckbox
                            name="skipArtworkOption"
                            label="Design later – skip this step and email artwork later"
                            description={
                              editorConfiguration?.skipArtworkOptionInstructions
                            }
                            className="mt-8"
                            labelClassName="text-acai"
                            colour="pitaya"
                            showDescription={
                              selectedDesignJourney ===
                              DESIGN_PATHS.SKIP_ARTWORK
                            }
                            initialValue={
                              selectedDesignJourney ===
                              DESIGN_PATHS.SKIP_ARTWORK
                            }
                            onChange={(event) => {
                              if (event.target.checked) {
                                setSelectedDesignJourney(
                                  DESIGN_PATHS.SKIP_ARTWORK
                                )
                              } else {
                                setSelectedDesignJourney(
                                  DESIGN_PATHS.DESIGN_UPLOAD
                                )
                              }
                            }}
                          />
                        )}

                        <div className="mt-4">
                          <RadioTileGroup
                            options={designPathsOptions}
                            optionName="Design"
                            onChange={(selectedOption) => {
                              if (needDesignJourneyChangeConfirmation) {
                                setPendingDesignJourneyChoice(selectedOption)
                              } else {
                                setSelectedDesignJourney(
                                  selectedOption.key as TDesignJourney
                                )
                              }
                            }}
                            selected={{
                              key: selectedDesignJourney,
                              displayLabel: designPathsOptions.find(
                                (path) => path.key === selectedDesignJourney
                              )?.displayLabel,
                            }}
                            radioGroupClassName={`relative mb-9 z-0 ${
                              designPathsOptions.length % 2 === 0
                                ? 'grid-cols-1 md:grid-cols-2'
                                : 'grid-cols-1 md:grid-cols-3'
                            }`}
                          />

                          <Transition
                            show={
                              !readOnlyMode &&
                              selectedDesignJourney !==
                                DESIGN_PATHS.SKIP_ARTWORK
                            }
                            enter="transition-all duration-300 ease-in-out"
                            enterFrom="opacity-0 max-h-0"
                            enterTo="opacity-100 max-h-72"
                            entered="transition-all duration-300 ease-in-out"
                            leave="transition-all duration-300 ease-in-out"
                            leaveFrom="opacity-100 max-h-72"
                            leaveTo="opacity-0 max-h-0"
                          >
                            <DesignerChatbot />
                          </Transition>

                          {selectedDesignJourney ===
                            DESIGN_PATHS.DESIGN_UPLOAD && (
                            <>
                              <GuidelineDownload
                                guidelineAssets={guidelineAssets}
                                selectedVariantOptions={selectedVariantOptions}
                                className="mb-12"
                              />
                            </>
                          )}

                          {selectedDesignJourney ===
                            DESIGN_PATHS.BRIEFING_FORM && (
                            <BriefingFormDataProvider
                              value={{
                                briefingFormOutput,
                                setBriefingFormOutput,
                                productOption,
                                preloadedAnswers: preloadedBriefConfig || null,
                                readOnlyMode,
                              }}
                            >
                              <BriefingForm
                                productSlug={productSlug}
                                contentConfig={parsedBriefConfiguration}
                              />
                              <p className="text-md text-black-60">
                                Our designers will prepare your proof with the
                                artwork&nbsp;<strong>you provide</strong>,
                                ensuring print-perfection and satisfaction.
                              </p>
                            </BriefingFormDataProvider>
                          )}

                          {selectedDesignJourney ===
                            DESIGN_PATHS.ONLINE_EDITOR && (
                            <UploadDropzone
                              idleStateContent={toUploadContent(
                                {
                                  ...uploaderIdleStateContent?.fields,
                                  h3: 'We prefer SVG, EPS, PDF (JPG/PNG are also accepted). Max file size: 100MB',
                                } as GenericContentBlock,
                                UPLOADER_FALLBACK_COPY[UploaderState.IDLE]
                              )}
                              successStateContent={toUploadContent(
                                uploaderSuccessStateContent?.fields,
                                UPLOADER_FALLBACK_COPY[UploaderState.SUCCESS]
                              )}
                              hoverStateContent={toUploadContent(
                                uploaderDragOverStateContent?.fields,
                                UPLOADER_FALLBACK_COPY[UploaderState.HOVER]
                              )}
                              upload={upload}
                              removeUploadFile={removeUploadFile}
                              className={
                                readOnlyMode
                                  ? 'pointer-events-none'
                                  : 'pointer-events-auto'
                              }
                              trackUploadedFile={trackUploadedFile}
                              selectable={true}
                              preselectedFileName={
                                preloadedOnlineEditorConfig?.selectedFileName
                              }
                              uploadType="svg-convert"
                            />
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mt-4">
                          <GuidelineDownload
                            guidelineAssets={guidelineAssets}
                            selectedVariantOptions={selectedVariantOptions}
                            className="mb-12"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-auto md:flex-1">
                {selectedDesignJourney === DESIGN_PATHS.ONLINE_EDITOR && (
                  <EditorWrapper
                    productOption={productOption}
                    urlSlug={urlSlug}
                    onDesignConfigChange={({
                      pdfGenerationPayload,
                      onlineEditorOrderPayload,
                    }) => {
                      setPdfGenerationPayload(pdfGenerationPayload)
                      setOnlineEditorOrderPayload(onlineEditorOrderPayload)
                    }}
                    preloadedOnlineEditorConfig={preloadedOnlineEditorConfig}
                    readOnlyMode={readOnlyMode}
                    announceDirtyForm={setNeedDesignJourneyChangeConfirmation}
                  />
                )}

                {selectedDesignJourney !== DESIGN_PATHS.ONLINE_EDITOR && (
                  <div className="sticky top-60 mb-36">
                    <UploadDropzone
                      idleStateContent={toUploadContent(
                        uploaderIdleStateContent?.fields,
                        UPLOADER_FALLBACK_COPY[UploaderState.IDLE]
                      )}
                      successStateContent={toUploadContent(
                        uploaderSuccessStateContent?.fields,
                        UPLOADER_FALLBACK_COPY[UploaderState.SUCCESS]
                      )}
                      hoverStateContent={toUploadContent(
                        uploaderDragOverStateContent?.fields,
                        UPLOADER_FALLBACK_COPY[UploaderState.HOVER]
                      )}
                      upload={upload}
                      removeUploadFile={removeUploadFile}
                      className={`min-h-[450px] mb-36 ${
                        readOnlyMode
                          ? 'pointer-events-none'
                          : 'pointer-events-auto'
                      }`}
                      trackUploadedFile={trackUploadedFile}
                      selectable={false}
                      disableUpload={
                        readOnlyMode ||
                        selectedDesignJourney === DESIGN_PATHS.SKIP_ARTWORK
                      }
                      uploadType="upload"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <Modal
            opened={!!pendingDesignJourneyChoice}
            size="small"
            title="Are you sure you want to switch design path?"
            subtitle="Any progress or input with this design path will be lost if you switch mode now."
            confirmLabel="Yes, continue"
            onConfirm={() => {
              clearSelectedDesignJourneyData(selectedDesignJourney)
              setSelectedDesignJourney(
                pendingDesignJourneyChoice.key as TDesignJourney
              )
              setPendingDesignJourneyChoice(null)
              setNeedDesignJourneyChangeConfirmation(false)
            }}
            onCancel={() => {
              setPendingDesignJourneyChoice(null)
            }}
          />

          <Modal
            opened={purchaseInProgress}
            size="large"
            showControls={{
              confirmButton: false,
              cancelButton: Boolean(addToCartError),
              cancelIcon: Boolean(addToCartError),
            }}
            onCancel={() => setPurchaseInProgress(false)}
          >
            <div className="flex flex-col items-center w-full max-w-lg mx-auto my-36">
              <img
                className="mb-6 w-52 h-52"
                src={
                  addToCartError
                    ? '/images/static-nellie.png'
                    : '/loading-nellie.gif'
                }
                alt="loading icon"
              />
              <h3 className="text-4xl font-semibold text-center">
                {addToCartError || `Hold on. We're packaging up your design...`}
              </h3>
            </div>
          </Modal>

          <SummaryStickyFooter
            price={formatPrice({
              amount: productOption?.price,
              currencyCode:
                productOption?.currencyCode ||
                NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
              locale: 'en-US',
            })}
            salePrice={
              productOption?.salePrice &&
              productOption?.salePrice < productOption?.price
                ? formatPrice({
                    amount: productOption?.salePrice,
                    currencyCode:
                      productOption?.currencyCode ||
                      NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
                    locale: 'en-US',
                  })
                : undefined
            }
            taxTag={NEXT_PUBLIC_TAX_TYPE ? ` +${NEXT_PUBLIC_TAX_TYPE}` : null}
            leftLink={{
              text: 'Back',
              onClick: () => {
                router.back()
              },
            }}
            variantOptions={[
              {
                key: 'Quantity',
                value: productOption?.quantity,
              },
            ]}
            callToActionButton={{
              text:
                query?.get('edit') === 'true' ? 'Update Cart' : 'Add to Cart',
              onClick: async () => {
                try {
                  setAddToCartError(null)
                  setPurchaseInProgress(true)
                  let onlineEditorConfig: any = {}

                  if (selectedDesignJourney === DESIGN_PATHS.ONLINE_EDITOR) {
                    onlineEditorConfig =
                      await getOnlineEditorCustomizationObject({
                        onlineEditorOrderPayload,
                        pdfGenerationPayload,
                        parentOrderDesignId:
                          preloadConfiguration?.onlineEditor?.bucketDesignId,
                      })
                  }

                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  await addItemToCart({
                    cartContent,
                    productCategorySlug: urlSlug || productSlug,
                    selection: productOptionSelected(),
                    brief:
                      !isEmpty(briefingFormOutput) &&
                      selectedDesignJourney === DESIGN_PATHS.BRIEFING_FORM
                        ? briefingFormOutput
                        : null,
                    onlineEditor:
                      selectedDesignJourney === DESIGN_PATHS.ONLINE_EDITOR
                        ? onlineEditorConfig
                        : null,
                    filesUrls:
                      selectedDesignJourney === DESIGN_PATHS.SKIP_ARTWORK
                        ? null
                        : getFileUploadedUrls(),
                    itemId: editingItemId(),
                    edit: query?.get('edit') as string,
                    designJourney: selectedDesignJourney as TDesignJourney,
                    qrCodeChoice,
                  })

                  openSidebar()
                  // reset the query params
                  router.push('')

                  clearUploadedFiles()
                  setBriefingFormOutput({})
                  setPreloadedBriefConfig(null)
                  setSelectedDesignJourney(DESIGN_PATHS.DESIGN_UPLOAD)
                  setPurchaseInProgress(false)
                } catch (err) {
                  setAddToCartError(
                    'Something went wrong! Please try again later.'
                  )
                  Sentry.captureException(err, {
                    extra: {
                      'noissue.everest': {
                        productSlug,
                        variantSku: productOption.variantSku,
                        variantId: productOption.variantId,
                        productId: productOption.productId,
                        name: productOption.name,
                        modifierId: productOption.modifierId,
                        currencyCode: productOption.currencyCode,
                      },
                    },
                  })
                }
              },
              disabled: !canPurchase || readOnlyMode || purchaseInProgress,

              loading: purchaseInProgress,
            }}
          />
        </UnifiedEditorProvider>
      </UploadDropzoneProvider>
    </>
  )
}
